import { MAIN_BACKGROUND } from "./constants/colors";
import { useFonts } from "expo-font";
import { StatusBar } from "expo-status-bar";
import { StyleSheet, View, Platform, Text, TextInput } from "react-native";
import Toast, { BaseToast, ErrorToast } from "react-native-toast-message";
// Import i18n configuration to initialize translations
import './config/i18n';
// Import conditionally based on platform
import * as SplashScreen from "expo-splash-screen";
import * as ScreenOrientation from "expo-screen-orientation";
// DISABLED: OneSignal
// import { LogLevel, OneSignal } from "react-native-onesignal";
import React, { useCallback, useEffect, useState } from "react";
import { NavigationContainer, getStateFromPath } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { ThemeProvider } from './theme/theme';
import RootNavigator from './navigation/RootNavigator';
import OnboardingDebugButton from './components/OnboardingDebugButton';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { MentorChatProvider } from './contexts/MentorChatContext';
import { LoggingProvider } from './contexts/LoggingContext';
import MinimalAudioPlayer from './components/AudioPlayer/MinimalAudioPlayer';
import { auth, onAuthStateChanged, setAnalyticsCollectionEnabled } from "./config/firebase";
import ErrorBoundary from './components/ErrorBoundary';
import { errorReporting } from './services/errorReportingService';
import { UpdatePrompt } from './components/UpdatePrompt';
import { checkVersion, VersionCheckResult, UpdateStatus } from './services/versionService';
import { sqliteCacheService } from './services/sqliteCacheService';

// Create a fallback for web platforms
const requestTrackingPermissionsAsync = async () => {
  // Only try to import on non-web platforms
  if (Platform.OS !== 'web') {
    try {
      // Using require instead of import for conditional loading
      const trackingModule = require('expo-tracking-transparency');
      return await trackingModule.requestTrackingPermissionsAsync();
    } catch (error) {
      console.warn('Error requesting tracking permissions:', error);
    }
  }
  // Return denied status for web or if module fails to load
  return { status: 'denied' };
};

// MainLayout removed - using RootNavigator instead
import { Provider } from "react-redux";
import store from "./store";

// Handle GoogleSignin based on platform
let GoogleSignin: any = {
  configure: () => {},
  hasPlayServices: async () => true,
  signIn: async () => ({}),
  isSignedIn: async () => false,
  signOut: async () => {},
};

// Only import GoogleSignin on non-web platforms
if (Platform.OS !== 'web') {
  try {
    GoogleSignin = require('@react-native-google-signin/google-signin').GoogleSignin;
    GoogleSignin.configure({
      // Get this from -> android/app/google-services.json , client/oauth_client/client_id (ending with apps.googleusercontent.com)
      webClientId:
        "349271191157-qundudnl96pjigi6v3dq3211h6r4jkme.apps.googleusercontent.com",
      offlineAccess: false, // We don't need offline access
      forceCodeForRefreshToken: false,
    });
  } catch (error) {
    console.warn('Error configuring GoogleSignin:', error);
  }
}

/*
  1. Create the config
*/
const toastConfig: any = {
  /*
    Overwrite 'success' type,
    by modifying the existing `BaseToast` component

    I can consume any custom `props` I want.
    They will be passed when calling the `show` method (see below)
  */
  success: (props: any) => (
    <BaseToast
      {...props}
      style={{
        borderLeftColor: "#3E00FA",
        borderColor: "#2A2A2A",
        borderWidth: 1,
        borderRadius: 6,
        backgroundColor: MAIN_BACKGROUND,
      }}
      contentContainerStyle={{
        backgroundColor: MAIN_BACKGROUND,
        borderRadius: 6,
      }}
      text1Style={{
        fontSize: 15,
        fontWeight: "400",
        color: "white",
      }}
    />
  ),
  /*
    Overwrite 'error' type,
    by modifying the existing `ErrorToast` component
  */
  error: (props: any) => (
    <ErrorToast
      {...props}
      style={{
        borderLeftColor: "#EF4444",
        borderColor: "#2A2A2A",
        borderWidth: 1,
        borderRadius: 6,
        backgroundColor: MAIN_BACKGROUND,
      }}
      contentContainerStyle={{
        backgroundColor: MAIN_BACKGROUND,
        borderRadius: 6,
      }}
      text1Style={{
        fontSize: 15,
        fontWeight: "400",
        color: "white",
      }}
    />
  ),
};

import "react-native-gesture-handler";
// DISABLED: OneSignal utilities
// import {
//   handleNotificationClick,
//   onBeforeNotification,
//   requestNotificationPermission,
//   shouldDisplayNotification,
// } from "utils/onesignal";

// Initialize Sentry via errorReportingService (better - has user context, breadcrumbs, etc.)
const SENTRY_DSN = 'https://9890786f61510ccabc6f49625aa5d67f@o4510585545752576.ingest.us.sentry.io/4510585547259904';
console.log('🔍 Initializing Sentry with DSN:', SENTRY_DSN.substring(0, 30) + '...');
errorReporting.initialize(SENTRY_DSN);

// Configure Expo Notifications Handler (for local notifications)
if (Platform.OS !== 'web') {
  const Notifications = require('expo-notifications');
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  });
}

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

export default function App() {
  // CRITICAL: Capture transfer token BEFORE React Navigation strips it (web only)
  // React Navigation's web history rewrites URLs, removing query parameters like ?token=xxx
  // We preserve the token in sessionStorage so TransferAuthScreen can access it
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      if (token && window.location.pathname.includes('/auth/transfer')) {
        sessionStorage.setItem('mentorist_transfer_token', token);
      }
    }
  }, []);

  const [fontsLoaded, fontError] = useFonts({
    // Poppins fonts - Primary font family used throughout the app
    "Poppins-Regular": require("./assets/fonts/Poppins-Regular.ttf"),
    "Poppins-Medium": require("./assets/fonts/Poppins-Medium.ttf"),
    "Poppins-SemiBold": require("./assets/fonts/Poppins-SemiBold.ttf"),
    "Poppins-Bold": require("./assets/fonts/Poppins-Bold.ttf"),
    // Outfit fonts - Used for reader/blog content
    "Outfit-Regular": require("./assets/fonts/Outfit-Regular.ttf"),
    "Outfit-Medium": require("./assets/fonts/Outfit-Medium.ttf"),
    "Outfit-SemiBold": require("./assets/fonts/Outfit-SemiBold.ttf"),
    "Outfit-Bold": require("./assets/fonts/Outfit-Bold.ttf"),
    // Inter fonts - Legacy support
    "Inter-Regular": require("./assets/fonts/Inter-Regular.ttf"),
    "Inter-Medium": require("./assets/fonts/Inter-Medium.ttf"),
    "Inter-SemiBold": require("./assets/fonts/Inter-SemiBold.ttf"),
    "Inter-Bold": require("./assets/fonts/Inter-Bold.ttf"),
  });
  const [appIsReady, setAppIsReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initializingAuth, setInitializingAuth] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [defaultStylesSet, setDefaultStylesSet] = useState(false);
  const [versionCheck, setVersionCheck] = useState<VersionCheckResult | null>(null);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  const setupDefaultTextStyles = useCallback(async () => {
    if (fontsLoaded && !defaultStylesSet) {
      const defaultStyle = {
        fontFamily: 'Poppins-Regular',
      };
      
      // Note: defaultProps is deprecated in React 18+
      // This is a legacy approach for React Native
      const Text = require('react-native').Text;
      const TextInput = require('react-native').TextInput;
      
      // Set default font for Text component
      const TextRender = Text.render;
      Text.render = function(props: any, ref: any) {
        return TextRender.call(this, {
          ...props,
          style: [defaultStyle, props.style]
        }, ref);
      };
      
      // Set default font for TextInput component  
      const TextInputRender = TextInput.render;
      TextInput.render = function(props: any, ref: any) {
        return TextInputRender.call(this, {
          ...props,
          style: [defaultStyle, props.style]
        }, ref);
      };

      setDefaultStylesSet(true);
    }
  }, [fontsLoaded, defaultStylesSet]);

  // Apply default text styles
  useEffect(() => {
    setupDefaultTextStyles();
  }, [setupDefaultTextStyles]);

  // Lock screen orientation to portrait only
  useEffect(() => {
    async function lockOrientation() {
      if (Platform.OS !== 'web') {
        try {
          await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
          console.log('✅ Screen orientation locked to portrait');
        } catch (error) {
          console.warn('Failed to lock screen orientation:', error);
        }
      }
    }
    lockOrientation();
  }, []);

  // Handle tracking permissions
  useEffect(() => {
    (async () => {
      // Get tracking permissions for iOS
      const { status: trackingStatus } =
        await requestTrackingPermissionsAsync();
      // We don't use tracking information, but we need to handle the request
      // to avoid warnings
    })();
  }, []);

  // DISABLED: Initialize OneSignal
  // useEffect(() => {
  //   (() => {
  //     if (Platform.OS !== 'web') {
  //       OneSignal.Debug.setLogLevel(LogLevel.Verbose);
  //       OneSignal.initialize(process.env.EXPO_PUBLIC_ONESIGNAL_APP_ID as string);

  //       requestNotificationPermission();
  //       OneSignal.Notifications.addEventListener(
  //         "click",
  //         handleNotificationClick
  //       );
  //       OneSignal.Notifications.addEventListener(
  //         "foregroundWillDisplay",
  //         async (event) => {
  //           event.preventDefault();
  //           await onBeforeNotification(event);

  //           if (shouldDisplayNotification(event)) {
  //             event.getNotification().display();
  //           }
  //         }
  //       );
  //     }
  //   })();
  // }, []);

  // Only use Firebase auth on non-web platforms
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const unsubscribe = onAuthStateChanged?.(auth, (user: { uid: string } | null) => {
        if (user) {
          // DISABLED: OneSignal login
          // OneSignal.login(user.uid);
        }
      });

      return unsubscribe;
    }
  }, []);

  // Handle Firebase authentication state
  useEffect(() => {
    const { isOnboardingComplete } = require('./utils/onboardingStorage');

    // Web OAuth uses Firebase v5 compat (authService.web.ts) which syncs to v12 auth automatically
    const unsubscribe = onAuthStateChanged?.(auth, async (user: any) => {
      console.log('🔐 [App] Auth state changed:', { 
        hasUser: !!user, 
        userId: user?.uid,
        email: user?.email,
        isAnonymous: user?.isAnonymous 
      });
      
      setIsAuthenticated(!!user);

      // Update error reporting user context
      if (user) {
        errorReporting.setUser(user.uid, user.email, user.displayName);
        
        // Set AppsFlyer user ID for attribution tracking
        if (Platform.OS !== 'web') {
          try {
            const { setAppsFlyerUserId } = require('./services/appsFlyerService');
            setAppsFlyerUserId(user.uid);
          } catch (error) {
            console.warn('Failed to set AppsFlyer user ID:', error);
          }
        }
        
        // Check for pending migration after OAuth page reload
        if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
          const pendingMigration = sessionStorage.getItem('mentorist_pending_migration');
          if (pendingMigration) {
            try {
              const { previousUserId, newUserId, oldProfileData } = JSON.parse(pendingMigration);
              if (newUserId === user.uid) {
                console.log('🔄 Processing post-reload migration');
                const { migrateAnonymousProfile } = require('./screens/Auth/authShared');
                await migrateAnonymousProfile(previousUserId, newUserId, oldProfileData);
                sessionStorage.removeItem('mentorist_pending_migration');
                console.log('✅ Post-reload migration complete');
              }
            } catch (err) {
              console.warn('⚠️ Failed to process pending migration:', err);
              sessionStorage.removeItem('mentorist_pending_migration');
            }
          }
        }
        
        // Initialize profile first (creates empty profile if doesn't exist)
        // This prevents cache misses during onboarding - profile exists but is empty
        const { PrivateProfileService } = require('./services/privateProfileService');
        await PrivateProfileService.initializeUserProfile(user.uid);
        
        // Check onboarding status (now profile exists, just need to check if it has data)
        const completed = await isOnboardingComplete();
        console.log('✅ [App] Onboarding check result:', { 
          userId: user.uid,
          completed,
          willShowDashboard: completed 
        });
        
        setHasCompletedOnboarding(completed);
      } else {
        errorReporting.clearUser();
        setHasCompletedOnboarding(false);
      }

      setInitializingAuth(false);
      setCheckingOnboarding(false);
    });
    
    return unsubscribe;
  }, []);

  // Poll for onboarding completion (DISABLED - onboarding explicitly calls markOnboardingComplete)
  // This aggressive polling was causing unnecessary Firestore reads while user is in onboarding flow
  // The onboarding completion is handled by:
  // 1. CalculationScreen calling markOnboardingComplete() after profile creation
  // 2. Auth state change listener checking isOnboardingComplete() on login/reload
  // 3. Manual navigation after onboarding completes
  // If we ever need polling again, use a much longer interval (30+ seconds)
  useEffect(() => {
    // Polling disabled - see comment above
    // if (isAuthenticated && !hasCompletedOnboarding) {
    //   console.log('🔍 [App.pollEffect] Starting onboarding polling interval');
    //   const checkInterval = setInterval(async () => {
    //     const { isOnboardingComplete } = require('./utils/onboardingStorage');
    //     const completed = await isOnboardingComplete();
    //     if (completed) {
    //       setHasCompletedOnboarding(true);
    //       clearInterval(checkInterval);
    //     }
    //   }, 30000); // 30 seconds if re-enabled
    //   return () => clearInterval(checkInterval);
    // }
  }, [isAuthenticated, hasCompletedOnboarding]);

  useEffect(() => {
    async function prepare() {
      try {
        // Initialize SQLite cache for books and insights
        if (Platform.OS !== 'web') {
          try {
            await sqliteCacheService.init();
          } catch (error) {
            console.error('Failed to initialize SQLite cache:', error);
            // Continue anyway - will fallback to Firebase only
          }
          
          // Initialize AppsFlyer for deep link attribution and install tracking
          try {
            const { initializeAppsFlyer } = require('./services/appsFlyerService');
            await initializeAppsFlyer(
              (deepLinkUrl: string, token?: string) => {
                console.log('[App] AppsFlyer deep link:', { deepLinkUrl, hasToken: !!token });
                // Deep link handling is automatic via appsFlyerService
                // Authentication happens automatically if token is present
              },
              (conversionData: any) => {
                console.log('[App] AppsFlyer conversion data:', conversionData);
              }
            );
          } catch (error) {
            console.error('Failed to initialize AppsFlyer:', error);
            // Continue anyway - AppsFlyer is not critical for app function
          }
        }
      } catch (e) {
        console.warn(e);
      } finally {
        // Tell the application to render
        setAppIsReady(true);
      }
    }

    prepare();
  }, []);

  useEffect(() => {
    if (appIsReady) {
      SplashScreen.hideAsync().then(() => null);
    }
  }, [appIsReady]);

  // INITIALIZING GOOGLE ANALYTICS
  useEffect(() => {
    if (Platform.OS !== 'web') {
      setAnalyticsCollectionEnabled?.(true);
    }
  }, []);

  // Check app version on startup
  useEffect(() => {
    async function performVersionCheck() {
      try {
        const result = await checkVersion();
        setVersionCheck(result);
        
        // Show prompt if needed
        if (result.shouldShowPrompt) {
          setShowUpdatePrompt(true);
        }
        
        // Log to Sentry for monitoring
        if (result.status !== UpdateStatus.UP_TO_DATE) {
          const isUrgent = result.status === UpdateStatus.HARD_UPDATE || result.status === UpdateStatus.EMERGENCY_UPDATE;
          const message = `Version check: ${result.status}`;
          
          if (isUrgent) {
            errorReporting.reportWarning(message, {
              currentVersion: result.currentVersion,
              minimumVersion: result.minimumVersion,
              recommendedVersion: result.recommendedVersion,
            }, {
              screen: 'App',
              action: 'version_check',
            });
          } else {
            errorReporting.trackEvent('version_check', {
              status: result.status,
              currentVersion: result.currentVersion,
              recommendedVersion: result.recommendedVersion,
            });
          }
        }
      } catch (error) {
        console.error('❌ [App] Version check failed:', error);
        errorReporting.reportError(error as Error, {
          screen: 'App',
          action: 'version_check',
          metadata: { feature: 'version-check' },
        });
      }
    }
    
    performVersionCheck();
  }, []);

  // Web: Prevent browser back button from breaking React Navigation
  // Fix for Sentry errors REACT-NATIVE-G and REACT-NATIVE-H
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    const handlePopState = (event: PopStateEvent) => {
      // Stop event from reaching React Navigation
      event.stopImmediatePropagation();
      
      // Immediately push forward to stay on current page
      window.history.pushState(null, '', window.location.pathname);
    };

    // Initialize with a history state
    window.history.pushState(null, '', window.location.pathname);
    
    // Use capture phase to intercept BEFORE React Navigation
    window.addEventListener('popstate', handlePopState, true);

    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, []);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  if (!appIsReady || initializingAuth || checkingOnboarding) {
    return null; // Or a loading screen
  }

  // Block app if hard update or emergency update required
  const isBlocked = versionCheck && (
    versionCheck.status === UpdateStatus.HARD_UPDATE ||
    versionCheck.status === UpdateStatus.EMERGENCY_UPDATE
  );

  if (isBlocked && versionCheck) {
    return (
      <ErrorBoundary>
        <SafeAreaProvider>
          <ThemeProvider>
            <View style={styles.container}>
              <UpdatePrompt
                result={versionCheck}
                onDismiss={() => {
                  // Hard/emergency updates cannot be dismissed
                }}
              />
            </View>
          </ThemeProvider>
        </SafeAreaProvider>
      </ErrorBoundary>
    );
  }


  // Deep linking configuration for external service transfers, AppsFlyer, and test automation  
  const linking = {
    prefixes: ['https://ai.mentorist.app', 'mentorist://', 'http://localhost:19006'],
    config: {
      screens: {
        TransferAuth: {
          path: 'auth/transfer',
          // Query parameters are automatically parsed by React Navigation
          // URL format: https://ai.mentorist.app/auth/transfer?token=xxx
          // Will be available as route.params.token
        },
        TestRunner: {
          path: 'test-runner',
          parse: {
            autoTest: (autoTest: string) => autoTest === 'true',
          },
        },
        // AppsFlyer install deep link (handled by appsFlyerService, but defined for React Navigation)
        // URL format: mentorist://install?token=xxx
        Main: 'install',
        // Other screens...
      },
    },
    // Custom state parser for deep linking
    // Note: On web, token is preserved in sessionStorage (see useEffect above)
    // This handles native platforms where query params work correctly
    getStateFromPath: (path: string, options: any) => {
      try {
        // Extract query parameters for native platforms
        let queryParams: URLSearchParams | null = null;
        
        if (path.includes('?')) {
          const [_, queryString] = path.split('?');
          queryParams = new URLSearchParams(queryString);
        } else if (path.includes('://')) {
          try {
            const url = new URL(path);
            queryParams = url.searchParams;
          } catch (e) {
            // Invalid URL, continue without query params
          }
        }
        
        // Get base state from React Navigation
        const state = getStateFromPath(path, options);
        
        // Add token to TransferAuth route params if found in query string
        if (state?.routes?.[0]?.name === 'TransferAuth' && queryParams?.has('token')) {
          const token = queryParams.get('token');
          const currentRoute = state.routes[0];
          const currentParams = currentRoute.params as { token?: string } | undefined;
          
          if (!currentParams?.token && token) {
            return {
              ...state,
              routes: state.routes.map((route: any) => {
                if (route.name === 'TransferAuth') {
                  return {
                    ...route,
                    params: { ...route.params, token },
                  };
                }
                return route;
              }),
            };
          }
        }
        
        return state;
      } catch (error) {
        console.warn('Deep link parsing error:', error);
        return getStateFromPath(path, options);
      }
    },
  };

  return (
    <ErrorBoundary>
      <SafeAreaProvider>
        <ThemeProvider>
          <Provider store={store}>
            <AudioPlayerProvider>
              <SubscriptionProvider>
                <MentorChatProvider>
                  <LoggingProvider>
                    <NavigationContainer linking={linking}>
                      <View style={styles.container}>
                        <RootNavigator isAuthenticated={isAuthenticated} hasCompletedOnboarding={hasCompletedOnboarding} />
                        <StatusBar style="light" />
                        <OnboardingDebugButton />
                        <MinimalAudioPlayer />
                      </View>
                    </NavigationContainer>
                    <Toast config={toastConfig} />
                    
                    {/* Soft update prompt (dismissible) */}
                    {versionCheck && showUpdatePrompt && (
                      <UpdatePrompt
                        result={versionCheck}
                        onDismiss={() => setShowUpdatePrompt(false)}
                      />
                    )}
                  </LoggingProvider>
                </MentorChatProvider>
              </SubscriptionProvider>
            </AudioPlayerProvider>
          </Provider>
        </ThemeProvider>
      </SafeAreaProvider>
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MAIN_BACKGROUND,
  },
});